import { createApi } from '@reduxjs/toolkit/query/react'

import { IForm } from 'core-components/classes/models/IForm'
import { ISitePage } from 'core-components/sitebuilder/models/ISitePage'
import { IWidgetsData } from 'core-components/sitebuilder/models/IWidgetData'
import {
  setSitePages,
  setSitePagesSettings,
} from 'core-components/sitebuilder/store/siteBuilderSlice'
import { checkCache, readFromCache, saveToCache } from 'src/core/classes/cache/Cache'
import StorageKeys from 'src/core/classes/cache/StorageKeys'
import { getAppId } from 'src/core/classes/helpers/getAppId'
import { proxySiteBuilder } from 'src/core/classes/helpers/UrlHelper'
import { map as mapSitePage } from 'src/core/classes/models/SitePage'
import VersionStoreService from 'src/core/classes/services/VersionStoreService'
import { RootState } from 'src/core/store'
import { getCustomQuery } from 'src/core/store/service/helpers/getCustomQuery'
import Logger from 'src/core/utils/Logger'

import { loadSitePageSettings } from './endpoints/pageApi/helpers/loadSitePageSettings'
import { persistSitePageLayoutData } from './endpoints/pageApi/helpers/persistSitePageLayoutData'

export const pageApi = createApi({
  reducerPath: '@app/api/sitePageApi',
  baseQuery: getCustomQuery({
    storageKey: StorageKeys.roles,
    baseUrl: proxySiteBuilder('/mbe/api'),
    cachedUrl: [],
  }),
  endpoints: (builder) => ({
    fetchSitePages: builder.query<ISitePage[], boolean>({
      async queryFn(force, { dispatch }, _extraOptions, fetchWithBQ) {
        const needRefresh = force || checkCache(StorageKeys.sitePages)
        const sitePagesSettings = await loadSitePageSettings()

        if (needRefresh) {
          const { data } = await fetchWithBQ('/v3/site-pages/extended')
          if (!data) {
            return { error: { status: 'CUSTOM_ERROR', error: 'Failed to fetch data' } }
          }

          const sitePages = ((data as ISitePage[]) || []).map(mapSitePage)
          dispatch(setSitePages(sitePages))
          dispatch(setSitePagesSettings(sitePagesSettings))

          if (sitePages?.length > 0) {
            await saveToCache(StorageKeys.sitePages, sitePages, true)
          }

          return { data: sitePages }
        } else {
          const cachedPages = readFromCache(StorageKeys.sitePages)
          if (cachedPages) {
            dispatch(setSitePages(cachedPages))
            dispatch(setSitePagesSettings(sitePagesSettings))
          }
          return { data: cachedPages }
        }
      },
    }),

    fetchSitePage: builder.query<ISitePage | null, string>({
      async queryFn(id, _api, _extraOptions, fetchWithBQ) {
        const url = `/v3/site-pages/extended`
        const result = await fetchWithBQ(url)

        if ((result as { error: unknown }).error || !('data' in result)) {
          return { error: { status: 'CUSTOM_ERROR', error: 'Failed to fetch data' } }
        }

        const data = (result.data || []) as ISitePage[]
        const page = data.map(mapSitePage).find((i) => i.id === id)
        return { data: page ?? null }
      },
    }),

    createSitePage: builder.mutation<unknown, { sitePage: ISitePage }>({
      async queryFn({ sitePage }, _api, _extraOptions, fetchWithBQ) {
        try {
          const url = '/v2/site-pages'
          const payload = mapToServer(sitePage)
          const response = await fetchWithBQ({
            url,
            method: 'POST',
            body: payload,
          })

          if ('error' in response) {
            throw new Error((response.error as any)?.message || 'Fail request for page creation')
          }

          VersionStoreService.updateApiVersion(StorageKeys.sitePages)

          return { data: response.data }
        } catch (error: unknown) {
          return { error: { status: 'CUSTOM_ERROR', error: (error as Error).message } }
        }
      },
    }),

    updateSitePage: builder.mutation<unknown, { sitePage: ISitePage }>({
      async queryFn({ sitePage }, _api, _extraOptions, fetchWithBQ) {
        try {
          const url = `/v2/site-pages/${sitePage.id}`
          const payload = mapToServer(sitePage, ['Id'])
          const response = await fetchWithBQ({
            url,
            method: 'PUT',
            body: payload,
          })

          if ('error' in response) {
            throw new Error((response.error as any)?.message || 'Fail request for update page')
          }

          VersionStoreService.updateApiVersion(StorageKeys.sitePages)

          return { data: response.data }
        } catch (error: unknown) {
          return { error: { status: 'CUSTOM_ERROR', error: (error as Error).message } }
        }
      },
    }),

    updateBulkSitePages: builder.mutation<unknown, { sitePages: ISitePage[] }>({
      async queryFn({ sitePages }, _api, _extraOptions, fetchWithBQ) {
        try {
          const url = `/v2/site-pages/bulk`
          const payload = sitePages.map((sitePage) => mapToServer(sitePage))
          const response = await fetchWithBQ({
            url,
            method: 'PUT',
            body: payload,
          })

          if ('error' in response) {
            throw new Error((response.error as any)?.message || 'Fail request for update pages')
          }

          VersionStoreService.updateApiVersion(StorageKeys.sitePages)

          return { data: response.data }
        } catch (error: unknown) {
          return { error: { status: 'CUSTOM_ERROR', error: (error as Error).message } }
        }
      },
    }),

    deleteSitePage: builder.mutation<{ success: boolean; id: string }, string>({
      async queryFn(id, _api, _extraOptions, fetchWithBQ) {
        try {
          const url = `/v2/site-pages/${id}`
          const response = await fetchWithBQ({
            url,
            method: 'DELETE',
            body: {},
          })

          if ('error' in response) {
            throw new Error((response.error as any)?.message || 'Fail request for delete page')
          }

          VersionStoreService.updateApiVersion(StorageKeys.sitePages)

          return { data: { success: true, id } }
        } catch {
          return { data: { success: false, id } }
        }
      },
    }),

    updateSitePageWidgets: builder.mutation<boolean, { sitePage: ISitePage }>({
      async queryFn({ sitePage }, _api, _extraOptions, fetchWithBQ) {
        const widgetsUrl = `/v2/site-pages/${sitePage.id}/widgets`
        const bulkUrl = `/v2/site-pages/${sitePage.id}/widgets/bulk`

        try {
          const getResult = await fetchWithBQ(widgetsUrl)
          if (!('data' in getResult)) throw new Error('Failed to load widgets')
          const originalWidgets = (getResult.data || []) as any[]

          const removedIds = originalWidgets
            .map((w) => w.WidgetId)
            .filter((id) => !sitePage.availableWidgetsData.some((wd: any) => wd.i === id))

          if (removedIds.length > 0) {
            await fetchWithBQ({
              url: widgetsUrl,
              method: 'DELETE',
              body: removedIds,
            })
            VersionStoreService.updateApiVersion(StorageKeys.sitePages)
          }

          const newWidgets = sitePage.availableWidgetsData.filter(
            (wd: any) => !originalWidgets.some((ow: any) => ow.WidgetId === wd.i)
          )

          if (newWidgets.length > 0) {
            const payload = createSitePageWidgetsPayload(sitePage.id, newWidgets)
            await fetchWithBQ({
              url: widgetsUrl,
              method: 'POST',
              body: payload,
            })
            VersionStoreService.updateApiVersion(StorageKeys.sitePages)
          }

          const updatedWidgets = sitePage.availableWidgetsData.filter((wd: any) =>
            originalWidgets.some((ow: any) => ow.WidgetId === wd.i)
          )

          if (updatedWidgets.length > 0) {
            const payload = createSitePageWidgetsPayload(sitePage.id, updatedWidgets)
            await fetchWithBQ({
              url: bulkUrl,
              method: 'PUT',
              body: payload,
            })
            VersionStoreService.updateApiVersion(StorageKeys.sitePages)
          }

          return { data: true }
        } catch (error: unknown) {
          return { error: { status: 'CUSTOM_ERROR', error: (error as Error).message } }
        }
      },
    }),

    updatePageLocal: builder.mutation<boolean, { sitePage: ISitePage }>({
      async queryFn({ sitePage }, { dispatch, getState }) {
        const state = getState() as RootState
        if (state.siteBuilder.sitePages !== null) {
          const otherSitePages = state.siteBuilder.sitePages.filter((i) => i.id !== sitePage.id)
          const nextSitePages = [...otherSitePages, sitePage]

          dispatch(setSitePages(nextSitePages))
          VersionStoreService.updateApiVersion(StorageKeys.sitePages)
          saveToCache(StorageKeys.sitePages, nextSitePages)
        }
        return { data: true }
      },
    }),

    updateUserPageLayout: builder.mutation<boolean, { layoutCode: string; settings: IForm }>({
      async queryFn({ layoutCode, settings }, { dispatch, getState }) {
        const state = getState() as RootState
        const currentSitePageId = state.siteBuilder.currentSitePageId
        const sitePages = state.siteBuilder.sitePages
        const sitePage = sitePages.find((i) => i.id === currentSitePageId)

        if (!sitePage) {
          return { error: { status: 'CUSTOM_ERROR', error: 'Current SitePage not found' } }
        }

        const nextSitePage: ISitePage = {
          ...sitePage,
          layoutUser: layoutCode,
          layoutUserSettings: settings,
        }

        const newSitePages = sitePages.map((el) =>
          el.id === currentSitePageId ? nextSitePage : el
        )
        try {
          await persistSitePageLayoutData(newSitePages)
          dispatch(setSitePages(newSitePages))
          saveToCache(StorageKeys.sitePages, newSitePages)
        } catch (err) {
          Logger.error(err)
        }

        return { data: true }
      },
    }),

    updateSitePageRoles: builder.mutation<boolean, ISitePage>({
      async queryFn(sitePage, _api, _extraOptions, fetchWithBQ) {
        try {
          const url = `/v2/site-pages/${sitePage.id}/roles`
          const response = await fetchWithBQ({
            url,
            method: 'PUT',
            body: sitePage.roleIds,
          })

          if ('error' in response) {
            throw new Error((response.error as any)?.message || 'Fail request for update roles')
          }

          VersionStoreService.updateApiVersion(StorageKeys.sitePages)

          return { data: true }
        } catch (error: unknown) {
          return { error: { status: 'CUSTOM_ERROR', error: (error as Error).message } }
        }
      },
    }),

    toggleSitePageRole: builder.mutation<
      boolean,
      { sitePage: ISitePage; roleId: string; isChecked: boolean }
    >({
      async queryFn({ sitePage, roleId, isChecked }, { dispatch }, _extraOptions, fetchWithBQ) {
        const roleIds = isChecked
          ? [...sitePage.roleIds, roleId]
          : sitePage.roleIds.filter((i) => i !== roleId)

        const fetchResult = await fetchWithBQ({
          url: `/v2/site-pages/${sitePage.id}/roles`,
          method: 'PUT',
          body: JSON.stringify(roleIds),
        })

        if (!(fetchResult as { error: unknown }).error) {
          await dispatch(
            pageApi.endpoints.updatePageLocal.initiate({
              sitePage: {
                ...sitePage,
                roleIds,
              },
            })
          )
          return { data: true }
        }

        return { data: false }
      },
    }),
  }),
})

const mapToServer = (sitePage: ISitePage, filterKeys?: string[]): IForm => {
  const redirectUrl = sitePage.redirectUrl
    ? JSON.stringify({
        url: sitePage.redirectUrl,
        windowInfo: sitePage.redirectWindow,
      })
    : ''
  const result = {
    Id: sitePage.id,
    ParentId: sitePage.parent || null,
    Title: sitePage.name,
    NodeType: sitePage.type,
    IconCssClass: sitePage.iconClass,
    Order: sitePage.order,
    AudienceFilter: sitePage.audience,
    RedirectTo: sitePage.redirectTo || null,
    RedirectOption: sitePage.redirectOption || null,
    PreserveRedirectParams: sitePage.redirectWithParams || false,
    Settings: JSON.stringify(sitePage.defaultSettings || {}),
    Layout: redirectUrl || sitePage.layout || '{}',
    LayoutSettings: JSON.stringify(sitePage.layoutSettings),
    HiddenInMenu: !!sitePage.hidden,
    Disabled: !!sitePage.disabled,
    AppId: getAppId(),
    RolesMode: sitePage.rolesMode,
    GroupName: sitePage.groupName,
  }
  if (filterKeys) {
    filterKeys.forEach((key) => {
      delete result[key]
    })
  }
  return result
}

const createSitePageWidgetsPayload = (sitePageId: string, sitePageWidgetData: IWidgetsData[]) => {
  return sitePageWidgetData.map((widgetData) => ({
    SitePageId: sitePageId,
    AppId: getAppId(),
    WidgetId: widgetData.i,
    Height: widgetData.h,
    Width: widgetData.w,
    X: widgetData.x,
    Y: widgetData.y,
    Area: widgetData.area,
  }))
}

export const {
  useFetchSitePagesQuery,
  useCreateSitePageMutation,
  useUpdateSitePageMutation,
  useUpdateBulkSitePagesMutation,
  useDeleteSitePageMutation,
  useUpdateSitePageWidgetsMutation,
  useUpdateUserPageLayoutMutation,
  useUpdateSitePageRolesMutation,
  useToggleSitePageRoleMutation,
} = pageApi
